import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

// App-wide in-app confirm dialog — replaces every window.confirm() so the
// browser's ugly admin-console-53.preview.emergentagent.com says popup never
// shows. Usage:
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Delete task?', description: 'This cannot be undone.', confirmText: 'Delete', variant: 'destructive' }))) return;
//
// Returns a promise that resolves true on confirm, false on cancel/overlay-dismiss.
// We render a single AlertDialog at the provider root so multiple callers
// can never stack modals on top of each other.

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState({});
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setCfg({
      title: 'Are you sure?',
      description: '',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      variant: 'default', // 'default' | 'destructive'
      ...options,
    });
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const resolve = (value) => {
    setOpen(false);
    if (resolverRef.current) { resolverRef.current(value); resolverRef.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) resolve(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{cfg.title}</AlertDialogTitle>
            {cfg.description && <AlertDialogDescription>{cfg.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => resolve(false)} data-testid="confirm-cancel">{cfg.cancelText}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resolve(true)}
              data-testid="confirm-ok"
              className={cfg.variant === 'destructive' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
            >
              {cfg.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}
